import { JSX, KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";

import { PageSection } from "@app/shared";
import { QESButton, QESRadioButton, QESRadioGroup, QESTextInput } from "@app/shared/qes-standards";
import { VolumeReportsContext } from "@app/reports/volume-reports/import/VolumeReportsContext";
import { VolumeReportsProject, WizardStepProps } from "@app/reports/volume-reports/import/interfaces";
import { ColumnConfig, numberSort, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { RoleRestricted, Roles } from "@app/user";
import { Waffles } from "@app/shared/waffles";
import { checkForEnter } from "@app/shared/utility";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel } from "@app/tags/types";

const projectViewOptions = ["Project", "Tag"] as const;
type ProjectView = typeof projectViewOptions[number];

interface SearchValue {
  filterString: string;
  filterType: ProjectView;
}

interface ProjectsTableData {
  [wafflesKeyColumnName]: string;
  countySpecialtyGroupCount: string;
  hasUnpublishedData: JSX.Element;
  membershipType: string;
  networkName: string;
  numberOfGaps: string;
  projectName: string;
  templateName: string;
  rowIsDisabled?: boolean;
}

interface TagToProjectsList {
  tag: string;
  projectsList: VolumeReportsProject[];
}

interface GroupTagsTableData {
  [wafflesKeyColumnName]: string;
  tagGroup: string;
  tagsInGroup: string[];
  tag: string;
  tagsToProjects: TagToProjectsList[];
  numberOfProjects: number;
}

/* eslint-disable sort-keys */
const groupTagsColumnConfig: ColumnConfig = {
  tagGroup: {
    displayText: "Tag Groups",
    testId: "tag-group",
  },
  tag: {
    displayText: "Tags",
    testId: "tags",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects"
  },
}
/* eslint-enable sort-keys */

const VolumeReportsSelectProjects = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(VolumeReportsContext);

  const [projectView, setProjectView] = useState<ProjectView>("Project");
  const [currentFilter, setCurrentFilter] = useState<SearchValue>();
  const [groupTagsList, setGroupTagsList] = useState<ManageResourceTagDisplayModel[]>();
  const [originTagGroupData, setOriginTagGroupData] = useState<GroupTagsTableData[]>();
  const [selectedTagGroups, setSelectedTagGroups] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);

  const resourceTaggingService = new ResourceTaggingService();

  const projectsColumnConfig: ColumnConfig = {
    countySpecialtyGroupCount: {
      displayText: "County/Specialty Groups",
      hidden: wizardContext.reportType != "providers",
      testId: "volume-reports-select-projects-table__county-specialty-groups"
    },
    hasUnpublishedData: {
      displayText: "",
      hidden: ["cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType),
      noSort: true,
      testId: "has-unpublished-data"
    },
    membershipType: {
      displayText: "Membership Type",
      hidden: ["cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType),
      testId: "membership-types"
    },
    networkName: {
      displayText: "Network Name",
      hidden: !["cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType),
      testId: "network-name",
    },
    numberOfGaps: {
      displayText: "Gaps",
      hidden: ["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType),
      testId: "gaps",
    },
    projectName: {
      displayText: "Projects",
      testId: "projects",
      tooltip:  wizardContext.reportType === "pci" ? "Select up to 25" : null
    },
    rowIsDisabled: {
      displayText: "",
      hidden: true,
      testId: "",
    },
    templateName: {
      displayText: "Template",
      testId: "template",
    }
  };

  const isTagView = useMemo(() => {
    return projectView === "Tag";
  }, [projectView]);


  useEffect(() => {
    resourceTaggingService.getClientProjectGroupTagInfo().then((response: ManageResourceTagDisplayModel[]) => {
      setGroupTagsList(response);
    });
  }, [])

  useEffect(() => {
    props.pageIsValid(wizardContext.projects.some((project) => project.isChecked));
  }, [wizardContext.projects]);

  /* eslint-disable sort-keys */
  const groupsTableData: GroupTagsTableData[] = useMemo(() => {
    if(!!searchRef?.current?.value) {
      const filteredGroupsTableData = originTagGroupData?.filter(group => group?.tagGroup?.toLocaleLowerCase().includes(searchRef?.current?.value.toLocaleLowerCase())
        || group.tagsToProjects.map(x => x.tag.toLocaleLowerCase()).includes(searchRef?.current?.value.toLocaleLowerCase()));

      const projectIds = filteredGroupsTableData.flatMap(group => group.tagsToProjects.flatMap(tagToProject => tagToProject.projectsList.flatMap(x => x.customProjectId)));

      wizardContext.projects.forEach(project => {
        if(projectIds.includes(project.customProjectId)) {
          project.isVisible = true;
        }
      });

      return filteredGroupsTableData?.map<GroupTagsTableData>((group) => {

          return ({
            [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-"),
            tagGroup: group.tagGroup,
            tag: "",
            tagsInGroup: group.tagsInGroup,
            tagsToProjects: group.tagsToProjects,
            numberOfProjects: group.numberOfProjects
          })
      })
    }

    let tagGroupsWithProjects: GroupTagsTableData[] = [];
    const distinctTagGroups = [ ...new Set(groupTagsList?.map(x => x.tagGroupName))];
    if(distinctTagGroups?.length > 0){
      distinctTagGroups?.forEach(tagGroup => {

        tagGroupsWithProjects.push({
          tagGroup: tagGroup,
          tag: "",
          tagsInGroup: [],
          tagsToProjects: [],
          numberOfProjects: 0,
          [wafflesKeyColumnName]: ""
        })
      });

      groupTagsList?.forEach(groupTag => {
        if(tagGroupsWithProjects.find(x => x.tagGroup === groupTag.tagGroupName).tagsInGroup.findIndex(x => x === groupTag.tagName) < 0){
          tagGroupsWithProjects.find(x => x.tagGroup === groupTag.tagGroupName).tagsInGroup.push(groupTag.tagName);
        }
      });

      wizardContext.projects.forEach(project => {
        if(project.isVisible){
          project.tags?.forEach(tag => {
            tagGroupsWithProjects.forEach(tagGroupWithProjects => {
              if(tagGroupWithProjects.tagsInGroup.includes(tag)) {
                if(tagGroupWithProjects.tagsToProjects.findIndex(x => x.tag.toLocaleLowerCase() === tag.toLocaleLowerCase()) >= 0) {
                  const tagIndex = tagGroupWithProjects.tagsToProjects.findIndex(x => x.tag === tag);
                  tagGroupWithProjects?.tagsToProjects[tagIndex]?.projectsList?.push(project);
                }
                else {
                  const newTagToProjects:TagToProjectsList = {
                    tag: tag,
                    projectsList: [project]
                  }
                  tagGroupWithProjects.tagsToProjects.push(newTagToProjects);
                }
              }
            })
          });
        }
      });
    }

    tagGroupsWithProjects.forEach(tagGroupWithProjects => {
      if(tagGroupWithProjects.tagsToProjects.length <= 0){
        tagGroupsWithProjects = tagGroupsWithProjects.filter(x => x.tagGroup != tagGroupWithProjects.tagGroup);
      }

    });


    const sortedByTagGroup = [...tagGroupsWithProjects].sort((a, b) => a.tagGroup.localeCompare(b.tagGroup));
    sortedByTagGroup.forEach(group => {
      group.tagsToProjects.sort((a,b) => a.tag.localeCompare(b.tag));
    });

    setOriginTagGroupData(sortedByTagGroup);

    return sortedByTagGroup.map<GroupTagsTableData>((group) => {
      let numProj = 0;
      group.tagsToProjects.forEach(tagToProjects => {
       numProj += tagToProjects.projectsList.length;
      });

      group.numberOfProjects = numProj;

        return ({
          [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-"),
          tagGroup: group.tagGroup,
          tag: "",
          tagsInGroup: group.tagsInGroup,
          tagsToProjects: group.tagsToProjects,
          numberOfProjects: group.numberOfProjects
        })
    })
  }, [groupTagsList, wizardContext.projects, wizardContext.tagLookup, searchRef]);
  /* eslint-enable sort-keys */

  const groupTagsExpansionData: Map<string, GroupTagsTableData[]> = useMemo(() => {
    const data = new Map<string, GroupTagsTableData[]>();
    groupsTableData
      .forEach(group => {
        /* eslint-disable sort-keys */
        data.set(group.tagGroup.replaceAll(" ", "-"), group.tagsToProjects.map((tagToProjects) => (
          {
            [wafflesKeyColumnName]: group.tagGroup.replaceAll(" ", "-")+":"+tagToProjects.tag.replaceAll(" ", "-"),
            tagGroup: "",
            tag: tagToProjects.tag,
            tagsInGroup: group.tagsInGroup,
            tagsToProjects: group.tagsToProjects,
            numberOfProjects: tagToProjects.projectsList.length
          }
        )));
        /* eslint-enable */
      });
    return data;
  }, [groupsTableData, wizardContext.projects, wizardContext.tagLookup]);

  const projectsTableData: ProjectsTableData[] = useMemo(() => {
    return wizardContext.projects.filter(((project) => project.isVisible)).map<ProjectsTableData>((project) => {
      /* eslint-disable sort-keys */
      return ({
        [wafflesKeyColumnName]: project.customProjectId.toString(),
        projectName: project.projectName,
        hasUnpublishedData: project.isUnpublishedResult ? (
          <RoleRestricted allowedRoles={[Roles.VIEWER, Roles.RESOURCE_ADMIN]}>
            {project.isUnpublishedResult && <span className="fs18 text-danger fa fa-eye" data-toggle="tooltip" title="Showing Unpublished Data" />}
          </RoleRestricted>
        ) : <></>,
        numberOfGaps: !["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(wizardContext.reportType) && project.gapCount.toLocaleString(),
        countySpecialtyGroupCount: project.countySpecialtyGroupCount.toLocaleString(),
        templateName: project.templateName,
        rowIsDisabled: project.rowIsDisabled,
        membershipType: project.isDualMembershipProject ? "Dual Membership" : "Single Membership",
        networkName: project.networkName,
      });
      /* eslint-enable */
    });
  }, [wizardContext.projects, groupsTableData]);

  const selectedProjectIds: string[] = useMemo(() => {
    return wizardContext.projects.filter((project) => project.isChecked).map((p) => p.customProjectId.toString());
  }, [wizardContext.projects]);

  const indeterminateKeys: string[] = useMemo(() => {
    const localIndeterminateKeys:string[] = [];
    const currentSelectedProjectIds = wizardContext.projects.filter((project) => project.isChecked).map((p) => p.customProjectId.toString());

    if(projectView === "Tag") {
      groupsTableData.forEach(group => {
        group.tagsToProjects.forEach(tagToProjects => {
          let matchingProjectIdCount = 0;
          tagToProjects.projectsList.forEach(project => {
            if(currentSelectedProjectIds.includes(project.customProjectId.toString())) {
              matchingProjectIdCount++;
            }
          });

          if(matchingProjectIdCount != tagToProjects.projectsList.length && matchingProjectIdCount > 0) {
            if(!localIndeterminateKeys.includes(group.tagGroup + ":" + tagToProjects.tag)) {
              localIndeterminateKeys.push(group.tagGroup + ":" + tagToProjects.tag);
            }
          }
          else if(matchingProjectIdCount == tagToProjects.projectsList.length && localIndeterminateKeys.includes(group.tagGroup + ":" + tagToProjects.tag)){
            localIndeterminateKeys.filter(x => x != group.tagGroup + ":" + tagToProjects.tag);
          }
        });
      });
    }

    return localIndeterminateKeys;
  }, [selectedProjectIds]);

  const numberOfProjectsInTagsView = useMemo(() => {
    // these are already filtered by the search value
    return Array.from(groupTagsExpansionData).reduce((result, [, values]) => {
      result += values.length;
      return result;
    }, 0);
  }, [groupTagsExpansionData]);

  const handleCheckboxChange = (selectedProjectIds: string[]) => {
    if(wizardContext.reportType === "pci" ){
      if(selectedProjectIds.length >= 25){
        wizardContext.projects.filter(p => !selectedProjectIds.includes(p.customProjectId.toString())).forEach(p => {
          p.rowIsDisabled = true;
        });
      }else{
        wizardContext.projects.forEach(p => {
          p.rowIsDisabled = false;
        });
      }
    }

    if(projectView === "Tag") {
      const selectedTagGroups: string[] = [];
      setSelectedTagGroups(selectedProjectIds);
      
      const selectedTags:string[] = [];
      selectedProjectIds.forEach(selection => {
        if(!selectedTagGroups.includes(selection.split(":")[0].replaceAll('-', ' '))) {
          selectedTagGroups.push(selection.split(":")[0].replaceAll('-', ' '));
        }
        if(!selectedTags.includes(selection.replaceAll('-', ' ').split(":")[1])) {
          selectedTags.push(selection.split(":")[1].replaceAll('-', ' '));
        }
      });

      const selectedProjectIdsList:string [] = [];
      selectedProjectIdsList.push(...selectedProjectIdsList , ...selectedProjectIds);
      groupsTableData.forEach(element => {

        if(selectedTagGroups.includes(element.tagGroup)) {
          element.tagsToProjects.forEach(tagToProjects => {
            if(selectedTags.includes(tagToProjects.tag)) {
              selectedProjectIdsList.push(...selectedProjectIdsList, ...tagToProjects.projectsList.map(x => x.customProjectId.toString()));
            }
          });
        }
      });

      const uniqeProjectIdsList:string[] = [...new Set(selectedProjectIdsList)];
      wizardContext.updateSelectedProjects(uniqeProjectIdsList);
    }
    else {
      wizardContext.updateSelectedProjects(selectedProjectIds);
    }
  }

  const hasTags = useMemo(() => {
    return Object.entries(wizardContext.tagLookup).length > 0;
  }, [wizardContext.tagLookup]);

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
      checkForEnter(e, () => updateSearchString(searchRef.current.value));
  };

  const updateSearchString = (value: string) => {
    wizardContext.updateProjectVisibility(value, isTagView);
    setCurrentFilter({
      filterString: value,
      filterType: projectView,
    });
  }

  const filterMessage = useMemo(() => {
    if (!currentFilter || currentFilter.filterString === "") {
      return null;
    }

    return `Currently filtered to ${currentFilter.filterType.toLowerCase()}s including "${currentFilter.filterString}"`;
  }, [currentFilter]);

  const projectViewRadioButtons = useMemo(() => {
    return projectViewOptions.map((opt) => {
      const key = opt.toLowerCase().replaceAll(" ", "-");
      return (
        <QESRadioButton
          key={opt}
          label={opt}
          testId={`volume-reports__project-view--${key}`}
          onChange={() => {
            setProjectView(opt)
          }}
          checked={projectView === opt}
          name="project-view"
        />
      );
    });
  }, [projectViewOptions, projectView]);

  const customSort = (rows: ProjectsTableData[], key: keyof ProjectsTableData, direction: SortDirections) => {
    const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (key) {
      case "numberOfGaps":
        return numberSort(rows, key, directionMultiplier);
      case "projectName":
      case "templateName":
      case "membershipType":
      case "networkName":
      default:
        return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
    }
  }

  const customSortTags = (rows: GroupTagsTableData[], key: keyof GroupTagsTableData, direction: SortDirections) => {
    const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (key) {
      case "tag":
      case "tagGroup":
      default:
        return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
    }
  }

  return (
    <PageSection>
      <div className="container mt-3">
        <fieldset className="row">
          <span className="label-s">Search By</span>
          <div className="col-12 d-flex pl-0">
            <QESRadioGroup
              renderInline={true}
              groupContainerClass="col-3 pl-0 d-flex align-items-center"
            >
              {projectViewRadioButtons}
            </QESRadioGroup>
            <QESTextInput
              id="filter-projects"
              testId="volume-reports__search-input"
              defaultValue=""
              qesRef={searchRef}
              label="Search By"
              hideLabel={true}
              onKeyUp={(e) => handleKeyUp(e)}
              containerClassName={`col-8`}
              className="bg-white"
            />
            <div className="col-1 d-inline-flex justify-content-center align-items-center">
              <QESButton
                qesType="secondary"
                onClick={() => updateSearchString(searchRef.current.value)}
                qesTestId="volume-reports__search-button"
              >
                Search
              </QESButton>
            </div>
          </div>
        </fieldset>
        <p>{filterMessage || <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />}</p>
      </div>

      <div className={wizardContext.reportType == 'pci' ? "pci-report-container container" : "container"}>
        {(!isTagView || hasTags) && (
          !isTagView ? (
            <Waffles<ProjectsTableData>
              columns={projectsColumnConfig}
              customSort={customSort}
              data={projectsTableData}
              disableProcessingOverlay={true}
              fullWidth={true}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={selectedProjectIds}
              showCheckboxes={true}
              testId="volume-reports-projects-table"
            />
          ) :
            (
              <Waffles<GroupTagsTableData>
                columns={groupTagsColumnConfig}
                customSort={customSortTags}
                data={groupsTableData}
                dataExpansions={groupTagsExpansionData}
                disableProcessingOverlay={true}
                dataExpansionsHaveIndividualCheckboxes={true}
                fullWidth={true}
                handleCheckboxChange={handleCheckboxChange}
                indeterminateKeys={indeterminateKeys}
                showExpansionCheckboxesOnly={true}
                selectedKeys={selectedTagGroups}
                showCheckboxes={true}
                testId="volume-reports-projects-table"
                totalNumberOfRecords={numberOfProjectsInTagsView}
              />
            )
        )}
        {isTagView && !hasTags && <p>None of the projects matching your selections have any tags.</p>}
      </div>
    </PageSection>
  );
};

export default VolumeReportsSelectProjects;
